import { s, type Infer, type Optional, type Schema } from './schema';
import { isPlainObject } from 'lodash';
import { isIsoDate } from 'framework/utils/hooks/useDateService';

const ID_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;
export const isoDate = s.string().refine(isIsoDate, "isn't a real date written as YYYY-MM-DD");

export const id = s
  .string()
  .refine(text => ID_PATTERN.test(text), "isn't an id: a letter, then letters, digits and “_”");

const reference = s.byKey('kind', {
  today: s.strictObject({ kind: s.literal('today') }),
  fieldValue: s.strictObject({ kind: s.literal('fieldValue'), fieldId: s.string() }),
});

export type Reference = Infer<typeof reference>;

export const isReference = (value: unknown): value is Reference => {
  return isPlainObject(value) && 'kind' in (value as object);
};

const OPERATORS_WITHOUT_VALUE = ['isFilled', 'isEmpty'] as const;

const OPERATORS_WITH_VALUE = [
  'anyOf',
  'noneOf',
  'allOf',
  'equals',
  'notEquals',
  'lessThan',
  'lessThanOrEqual',
  'greaterThan',
  'greaterThanOrEqual',
  'contains',
  'notContains',
  'startsWith',
  'endsWith',
] as const;

export type Operator = (typeof OPERATORS_WITHOUT_VALUE | typeof OPERATORS_WITH_VALUE)[number];

export const operatorTakesValue = (operator: Operator): boolean => {
  return !OPERATORS_WITHOUT_VALUE.some(each => each === operator);
};

const comparisonValue = s.union([s.array(s.string()), s.number(), s.string(), reference]);

export type ComparisonValue = Infer<typeof comparisonValue>;

const comparisonWithoutValue = s.strictObject({
  kind: s.literal('comparison'),
  fieldId: s.string(),
  operator: s.enum(OPERATORS_WITHOUT_VALUE),
});

const comparisonWithValue = s.strictObject({
  kind: s.literal('comparison'),
  fieldId: s.string(),
  operator: s.enum(OPERATORS_WITH_VALUE),
  value: comparisonValue,
});

const comparison = s.byKey('operator', {
  ...Object.fromEntries(OPERATORS_WITHOUT_VALUE.map(operator => [operator, comparisonWithoutValue])),
  ...Object.fromEntries(OPERATORS_WITH_VALUE.map(operator => [operator, comparisonWithValue])),
});

export type Comparison = Infer<typeof comparison>;

const GROUP_KINDS = ['and', 'or'] as const;

export type Condition = Comparison | { kind: (typeof GROUP_KINDS)[number]; conditions: Condition[] };

const conditionGroup = s.strictObject({
  kind: s.enum(GROUP_KINDS),
  conditions: s.array(s.lazy(() => condition)).refine(list => list.length > 0, 'has no conditions'),
});

export const condition: Schema<Condition> = s.byKey('kind', { comparison, and: conditionGroup, or: conditionGroup });

export const comparisonsOf = (item: Condition): Comparison[] => {
  return item.kind === 'comparison' ? [item] : item.conditions.flatMap(comparisonsOf);
};

export const option = s.strictObject({ id, label: s.string() });

export type Option = Infer<typeof option>;

const bound = s.union([s.number(), s.string(), reference]);

export type Bound = Infer<typeof bound>;

export const VALIDATION_KINDS = ['required', 'range', 'unique', 'pattern'] as const satisfies ValidationKind[];

const validationOptions: { when: Optional<Condition>; message: Optional<string> } = {
  when: condition.optional(),
  message: s.string().optional(),
};

export const validation = s.byKey('kind', {
  required: s.strictObject({ kind: s.literal('required'), ...validationOptions }),
  range: s.strictObject({
    kind: s.literal('range'),
    min: bound.optional(),
    max: bound.optional(),
    ...validationOptions,
  }),
  unique: s.strictObject({ kind: s.literal('unique'), ...validationOptions }),
  pattern: s.strictObject({ kind: s.literal('pattern'), regex: s.string(), ...validationOptions }),
});

export type Validation = Infer<typeof validation>;
export type ValidationKind = Validation['kind'];
export type RangeValidation = Extract<Validation, { kind: 'range' }>;
export const PHONE_PATTERN = /^\+\d{7,15}$/;
